import { useMemo, useState } from 'react';

import { usePagination } from '../../../common/hooks/usePagination';
import { useTableSort } from '../../../common/hooks/useTableSort';
import { useDepartments } from '../../../hooks/useDepartments';
import { useDistricts } from '../../../hooks/useDistricts';
import { useProvinces } from '../../../hooks/useProvinces';
import {
  useManagedProviders,
  useRemoveProvider,
  useSaveProvider,
} from '../../../hooks/useProviders';
import { getApiErrorMessage } from '../../../utils/getApiErrorMessage';
import { sortRows } from '../../../utils/sortRows';
import { PROVIDER_TEXTS as TEXTS, PROVIDERS_PER_PAGE } from '../constants/providersConstants';
import { buildProviderForm } from '../utils/buildProviderForm';
import { buildProviderPayload } from '../utils/buildProviderPayload';
import { filterProviders } from '../utils/filterProviders';
import { validateProviderForm } from '../utils/validateProviderForm';

/** Cambiar provincia limpia departamento y localidad; cambiar departamento limpia la localidad. */
const LOCATION_RESETS = {
  uuid_province: { uuid_department: '', uuid_district: '' },
  uuid_department: { uuid_district: '' },
};

/**
 * Hook de la pantalla de proveedores: lista filtrada, ordenada y paginada en el navegador,
 * drawer de alta y edición y quitar de proveedores con confirmación.
 * @returns {Object} Estado de vista y handlers para la página.
 */
export const useProvidersPage = () => {
  const [search, setSearch] = useState('');
  const { page, setPage, rowsPerPage, handleRowsPerPageChange } = usePagination(PROVIDERS_PER_PAGE);
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [providerToRemove, setProviderToRemove] = useState(null);
  const tableSort = useTableSort(() => setPage(1));

  const query = useManagedProviders();
  const { data: provinces = [] } = useProvinces();
  const { data: departments = [] } = useDepartments();
  const { data: districts = [] } = useDistricts();
  const saveMutation = useSaveProvider();
  const removeMutation = useRemoveProvider();

  const filtered = useMemo(
    () => sortRows(filterProviders(query.data || [], search), tableSort.sort),
    [query.data, search, tableSort.sort]
  );
  const rows = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const openDrawer = (provider = null) => {
    setForm(buildProviderForm(provider, { departments, districts }));
    setErrors({});
    setError('');
  };

  const closeDrawer = () => {
    if (saveMutation.isPending) return;
    setForm(null);
  };

  const changeField = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value ?? '', ...(LOCATION_RESETS[name] || {}) }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const save = async () => {
    const validation = validateProviderForm(form);
    setErrors(validation);
    if (Object.keys(validation).length) return;
    try {
      await saveMutation.mutateAsync({ id: form.uuid, data: buildProviderPayload(form) });
      setForm(null);
    } catch (failure) {
      setError(getApiErrorMessage(failure, TEXTS.saveError));
    }
  };

  const confirmRemove = async () => {
    try {
      await removeMutation.mutateAsync(providerToRemove.uuid);
    } catch (failure) {
      setError(getApiErrorMessage(failure, TEXTS.removeError));
    } finally {
      setProviderToRemove(null);
    }
  };

  return {
    cancelRemove: () => setProviderToRemove(null),
    changeField,
    clearError: () => setError(''),
    closeDrawer,
    confirmRemove,
    departments: departments.filter((item) => item.uuid_province === form?.uuid_province),
    districts: districts.filter((item) => item.uuid_department === form?.uuid_department),
    drawerOpen: Boolean(form),
    error,
    errors,
    form,
    handlePageChange: (_, value) => setPage(value),
    handleSearch: (value) => {
      setSearch(value);
      setPage(1);
    },
    isLoading: query.isLoading,
    isRemoving: removeMutation.isPending,
    isSaving: saveMutation.isPending,
    loadError: query.isError ? getApiErrorMessage(query.error, TEXTS.loadError) : '',
    openCreate: () => openDrawer(null),
    openEdit: openDrawer,
    page,
    rowsPerPage,
    onRowsPerPageChange: handleRowsPerPageChange,
    providerToRemove,
    provinces,
    refetch: query.refetch,
    requestRemove: setProviderToRemove,
    rows,
    save,
    search,
    sort: tableSort.sort,
    sortTable: tableSort.toggleSort,
    totalCount: filtered.length,
  };
};
